import { CommonEventOptions } from '@westwing/appshell_types';
import { useState } from 'react';
import {
    OverlayDismissLabel,
    FormFieldTracking,
    FormLinkTracking,
    OverlayActions,
    FormInteractions,
    OverlayType,
    SIGNUP_AND_LOGIN_EVENT,
    OverlayShownLabel,
} from 'Interfaces/overlayTracking';
import { useGaTracking } from 'AppShell/TrackingHelper';
import { eventOptionsToString } from 'Helpers/trackingHelper';
import { LoginFormTrackingHook } from 'Interfaces/LoginFormTrackingHook';
import { trackingCategories } from 'Hooks/useSignupFormTracking';
import isClubMfPath from 'SharedHelpers/isClubMfPath';

const useLoginFormTracking = (): LoginFormTrackingHook => {
    const { trackGenericEvent } = useGaTracking();
    const isClub = isClubMfPath();

    const [trackedEvents, setTrackedEvents] = useState<{ [event: string]: boolean }>({});

    const trackLoginForm = (options: CommonEventOptions) => {
        trackGenericEvent(OverlayType.LoginOverlay, options);
    };

    const trackLoginFormOnce = (options: CommonEventOptions) => {
        const eventHash = eventOptionsToString(options);
        if (!trackedEvents[eventHash]) {
            trackLoginForm(options);
            const nextEvents = trackedEvents;
            nextEvents[eventHash] = true;
            setTrackedEvents(nextEvents);
        }
    };
    const trackOverlayShown = (label: OverlayShownLabel) => {
        if (isClub) {
            trackGenericEvent(trackingCategories.LOGIN_OVERLAY, {
                action: 'Login Form Impression',
            });
            return;
        }
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.OverlayShown,
            label,
        });
    };
    const trackOverlayDismiss = (label: OverlayDismissLabel) => {
        if (isClub) {
            trackGenericEvent(trackingCategories.LOGIN_OVERLAY, {
                action: 'Login Form Dismissed',
                label,
            });

            return;
        }
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.OverlayDismissed,
            label,
        });
    };

    const trackFieldFilled = (field: FormFieldTracking) => {
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldChanged,
            label: field,
        });
    };

    const trackFieldError = (field: FormFieldTracking) => {
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldError,
            label: field,
        });
    };

    const trackLinkClick = (link: FormLinkTracking) => {
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormLinkClicked,
            label: link,
        });
    };
    const trackFormSubmit = () => {
        trackLoginFormOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.SubmitButtonClicked,
        });
    };

    return {
        trackOverlayShown,
        trackOverlayDismiss,
        trackFieldFilled,
        trackFieldError,
        trackLinkClick,
        trackFormSubmit,
    };
};

export default useLoginFormTracking;
